"use client";

import { c as _c } from "react/compiler-runtime";
import { ChainsProvider, CurrenciesProvider } from "@opensea/app-kit";
import { GraphQLProvider } from "@opensea/graphql";
import { MediaContextProvider, SizeProvider, ToastProvider, ThemeProvider as UIKitThemeProvider } from "@opensea/ui-kit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { WagmiProvider } from "wagmi";
import { FlowHandler } from "../flow";
import { MfaHandler } from "../mfa";
import { AccountEffects } from "./AccountEffects";
import { BalancesProvider } from "./BalancesProvider";
import { FlagProvider } from "./FlagProvider";
import { IntlProvider } from "./IntlProvider";
import { NavigationProvider } from "./NavigationProvider";
import { RenderImageProvider } from "./RenderImageProvider";
import { SiweProvider } from "./SiweProvider";
import { SolanaWalletProvider } from "./SolanaWalletProvider";
import { TrackErrorProvider } from "./TrackErrorProvider";
import { TrackEventProvider } from "./TrackEventProvider";
/**
 * Contains the full feature set required for styling, fetching, and authentication to work.
 * As of now this is only used in storybook.
 */
export const WalletProvider = t0 => {
  const $ = _c(26);
  const {
    children,
    theme,
    renderLink,
    renderImage,
    chains,
    siweAdapter,
    initialCurrencies,
    acceptTermsEnabled,
    trackError,
    locale,
    flags,
    flagsReady,
    trackEvent,
    wagmiConfig,
    apiUrl,
    wsUrl
  } = t0;
  const [queryClient] = useState(_temp);
  let t1;
  if ($[0] !== acceptTermsEnabled || $[1] !== chains || $[2] !== children || $[3] !== flags || $[4] !== flagsReady || $[5] !== initialCurrencies || $[6] !== renderImage || $[7] !== renderLink || $[8] !== siweAdapter || $[9] !== trackError || $[10] !== trackEvent || $[11] !== wagmiConfig) {
    t1 = <ToastProvider><CoreWalletProvider acceptTermsEnabled={acceptTermsEnabled} chains={chains} flags={flags} flagsReady={flagsReady} initialCurrencies={initialCurrencies} renderImage={renderImage} renderLink={renderLink} siweAdapter={siweAdapter} trackError={trackError} trackEvent={trackEvent} wagmiConfig={wagmiConfig}>{children}</CoreWalletProvider></ToastProvider>;
    $[0] = acceptTermsEnabled;
    $[1] = chains;
    $[2] = children;
    $[3] = flags;
    $[4] = flagsReady;
    $[5] = initialCurrencies;
    $[6] = renderImage;
    $[7] = renderLink;
    $[8] = siweAdapter;
    $[9] = trackError;
    $[10] = trackEvent;
    $[11] = wagmiConfig;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== locale || $[14] !== t1) {
    t2 = <IntlProvider locale={locale}>{t1}</IntlProvider>;
    $[13] = locale;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== apiUrl || $[17] !== t2 || $[18] !== wsUrl) {
    t3 = <GraphQLProvider apiUrl={apiUrl} errorHandler={undefined} wsUrl={wsUrl}>{t2}</GraphQLProvider>;
    $[16] = apiUrl;
    $[17] = t2;
    $[18] = wsUrl;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== queryClient || $[21] !== t3) {
    t4 = <MediaContextProvider><SizeProvider><QueryClientProvider client={queryClient}>{t3}</QueryClientProvider></SizeProvider></MediaContextProvider>;
    $[20] = queryClient;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t4 || $[24] !== theme) {
    t5 = <UIKitThemeProvider theme={theme}>{t4}</UIKitThemeProvider>;
    $[23] = t4;
    $[24] = theme;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
};

/**
 * This set of wallet providers contains the minimal amount needed to authenticate
 * and render the proper flows. For styling and layout to work properly, it is expected
 * for the client app to provide the relevant providers to ensure the flow works properly,
 * such as Theming, Media, and GraphQL.
 *
 * You must handle i18n in the client app through using the useMessages hook.
 */
export const CoreWalletProvider = t0 => {
  const $ = _c(46);
  const {
    children,
    siweAdapter,
    renderLink,
    renderImage,
    chains,
    initialCurrencies,
    initialBalances,
    acceptTermsEnabled: t1,
    trackError,
    trackEvent,
    flags,
    flagsReady,
    wagmiConfig
  } = t0;
  const acceptTermsEnabled = t1 === undefined ? false : t1;
  const [queryClient] = useState(_temp2);
  let t2;
  if ($[0] !== acceptTermsEnabled || $[1] !== flags) {
    t2 = {
      ...flags,
      acceptTermsEnabled
    };
    $[0] = acceptTermsEnabled;
    $[1] = flags;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MfaHandler />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== acceptTermsEnabled) {
    t4 = <FlowHandler acceptTermsEnabled={acceptTermsEnabled} />;
    $[4] = acceptTermsEnabled;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== acceptTermsEnabled || $[7] !== siweAdapter || $[8] !== trackError) {
    t5 = <AccountEffects acceptTermsEnabled={acceptTermsEnabled} siweAdapter={siweAdapter} trackError={trackError} />;
    $[6] = acceptTermsEnabled;
    $[7] = siweAdapter;
    $[8] = trackError;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== siweAdapter || $[12] !== t4 || $[13] !== t5) {
    t6 = <SolanaWalletProvider><SiweProvider siweAdapter={siweAdapter}>{children}{t3}{t4}{t5}</SiweProvider></SolanaWalletProvider>;
    $[10] = children;
    $[11] = siweAdapter;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== renderImage || $[16] !== t6) {
    t7 = <RenderImageProvider renderImage={renderImage}>{t6}</RenderImageProvider>;
    $[15] = renderImage;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== renderLink || $[19] !== t7) {
    t8 = <NavigationProvider renderLink={renderLink}>{t7}</NavigationProvider>;
    $[18] = renderLink;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== initialBalances || $[22] !== t8) {
    t9 = <BalancesProvider initialBalances={initialBalances}>{t8}</BalancesProvider>;
    $[21] = initialBalances;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== initialCurrencies || $[25] !== t9) {
    t10 = <CurrenciesProvider initialCurrencies={initialCurrencies}>{t9}</CurrenciesProvider>;
    $[24] = initialCurrencies;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== chains || $[28] !== t10) {
    t11 = <ChainsProvider chains={chains}>{t10}</ChainsProvider>;
    $[27] = chains;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== trackEvent) {
    t12 = <TrackEventProvider trackEvent={trackEvent}>{t11}</TrackEventProvider>;
    $[30] = t11;
    $[31] = trackEvent;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12 || $[34] !== trackError) {
    t13 = <TrackErrorProvider trackError={trackError}>{t12}</TrackErrorProvider>;
    $[33] = t12;
    $[34] = trackError;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== flagsReady || $[37] !== t13 || $[38] !== t2) {
    t14 = <FlagProvider flags={t2} flagsReady={flagsReady}>{t13}</FlagProvider>;
    $[36] = flagsReady;
    $[37] = t13;
    $[38] = t2;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== queryClient || $[41] !== t14) {
    t15 = <QueryClientProvider client={queryClient}>{t14}</QueryClientProvider>;
    $[40] = queryClient;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15 || $[44] !== wagmiConfig) {
    t16 = <WagmiProvider config={wagmiConfig}>{t15}</WagmiProvider>;
    $[43] = t15;
    $[44] = wagmiConfig;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
function _temp() {
  return new QueryClient();
}
function _temp2() {
  return new QueryClient();
}